import type { ExtractResource, ResourceType } from '@medplum/fhirtypes'
import { getReferenceString } from '@seen/core/references'
import {
  getSearchKey,
  normalizeQuery,
  type QueryTypes,
} from '@seen/core/search'
import { recordEntries } from '@seen/core/utils'
import { useQueries } from '@tanstack/react-query'
import type { Expression } from 'fuse.js'
import { mapValues, omitBy } from 'lodash-es'
import { useEffect, useReducer } from 'react'
import {
  type CacheSearchParam,
  useCacheSearcher,
} from '#src/hooks/useCacheSearcher.ts'
import { useDebounced } from '#src/hooks/useDebounced.ts'
import { useErrorToast } from '#src/hooks/useErrorToast.ts'
import {
  getFhirSearchQueryOptions,
  type UseFhirQueryOptions,
} from '#src/hooks/useFhirSearch.ts'
import { useMedplum } from '#src/hooks/useMedplum.ts'
import { useMemoValue } from '#src/hooks/useMemoValue.ts'
import type { FuzzySearcher } from '#src/search/FuzzySearcher.ts'

export type UseUserInputSearchParam<T extends ResourceType> =
  CacheSearchParam<T>

export type UseUserInputSearchQuery<T extends ResourceType> = {
  [K in T]: UseUserInputSearchParam<K>
}

export function useUserInputSearch<T extends ResourceType>(
  userInput: string,
  query: UseUserInputSearchQuery<T>
): {
  resources: ExtractResource<T>[]
  scoresByReferenceString: Record<string, number>
} {
  // Debounce to reduce network requests.
  const memoizedParams = useMemoValue({ userInput, query })
  const { userInput: debouncedUserInput, query: debouncedQuery } = useDebounced(
    memoizedParams,
    100
  )

  useFetchQuery(debouncedQuery)

  // Avoid passing user input as query into useCacheSearcher(query). See useCacheSearcher.ts for details.
  const queryWithoutUserInput = mapValues(debouncedQuery, (searchParam) =>
    omitBy(searchParam, (value) => value === debouncedUserInput)
  )
  const cacheSearcher = useCacheSearcher(queryWithoutUserInput)

  return useSearchResults(cacheSearcher, userInput)
}

function useFetchQuery<T extends ResourceType = ResourceType>(
  query: UseUserInputSearchQuery<T>
): void {
  const searchQueries = useMemoValue(
    recordEntries(query).map(([resourceType, searchParam]) => {
      const { indexers: _, ...queryTypes } = searchParam
      return {
        type: resourceType,
        query: queryTypes as QueryTypes,
      }
    })
  )
  const medplum = useMedplum()
  const results = useQueries({
    queries: searchQueries.map(({ type, query }): UseFhirQueryOptions<T> =>
      getFhirSearchQueryOptions({ type, query, medplum })
    ),
  })
  const errorIndex = results.findIndex(({ error }) => !!error)
  const errorResult = errorIndex !== -1 ? results[errorIndex] : null
  const errorQuery = searchQueries[errorIndex]
  useErrorToast(
    errorResult?.error &&
      `Error accessing ${getSearchKey(
        medplum,
        errorQuery.type,
        normalizeQuery(errorQuery.query)
      )}: ${errorResult.error.message ?? 'Unknown error'}`
  )
}

function useSearchResults<T extends ResourceType>(
  cacheSearcher: FuzzySearcher<T>,
  pattern: string | Expression
): {
  resources: ExtractResource<T>[]
  scoresByReferenceString: Record<string, number>
} {
  const [results, updateResults] = useReducer(
    () => cacheSearcher.search(pattern),
    []
  )

  useEffect(() => {
    cacheSearcher.subscribe(updateResults)
    return () => cacheSearcher.unsubscribe()
  }, [cacheSearcher])

  const patternMemo = useMemoValue(pattern)
  useEffect(updateResults, [patternMemo, updateResults])

  return useMemoValue({
    resources: results.map((r) => r.resource),
    scoresByReferenceString: Object.fromEntries(
      results.map((r) => [getReferenceString(r.resource), r.score])
    ),
  })
}
